import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import { useApi } from '../lib/useApi';
import { apiRequest, ApiError } from '../lib/api';
import { Alert, Button, Field, Select, TextInput } from '../components/ui';
import '../styles/public.css';
import '../styles/insights.css';
import '../styles/blog.css';

/**
 * Share your story.
 *
 * The page says twice, in plain words, that nothing is published automatically
 * — once before the form and once at the point of consent. The brief asks for
 * consent controls; a tick-box nobody read is not consent, so the sentence
 * beside it says exactly what is being agreed to and the box is never
 * pre-ticked.
 */

const CONSENT =
  'I confirm this is my own experience, that I am happy for NyumbaSuite to edit '
  + 'it for length and clarity, and that it may be published on the NyumbaSuite website.';

interface Category { id: string; slug: string; name: string }

export function ShareStory() {
  const cats = useApi<{ categories: Category[] }>('/api/v1/content/categories');
  const [form, setForm] = useState({
    fullName: '', email: '', phone: '', roleLabel: '', title: '', body: '', categoryId: '',
  });
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const words = form.body.trim().split(/\s+/).filter(Boolean).length;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true); setError(null);
    try {
      await apiRequest('/api/v1/content/stories', {
        method: 'POST',
        body: {
          fullName: form.fullName,
          email: form.email || undefined,
          phone: form.phone || undefined,
          roleLabel: form.roleLabel || undefined,
          title: form.title,
          body: form.body,
          categoryId: form.categoryId || undefined,
          consent: true,
        },
      });
      setSent(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <>
        <PublicHeader />
        <div className="ins-story-page">
          <Alert tone="success">
            <strong>Thank you.</strong> Your story is with our editors. We read every one,
            and we will be in touch before anything is published.
          </Alert>
          <div style={{ marginTop: 24 }}>
            <Link className="btn btn-primary" to="/blog">Back to Blog</Link>
          </div>
        </div>
        <SiteFooter />
      </>
    );
  }

  return (
    <>
      <PublicHeader />

      <div className="ins-story-page">
        <h1>Share your story</h1>
        <p className="ins-story-lede">
          Landlords, tenants, caretakers and professionals — what have you learned
          running, renting or servicing property in Kenya? The useful stories are
          usually the ordinary ones: what went wrong, and what you did about it.
        </p>
        <Alert tone="info">
          Nothing is published automatically. An editor reads every submission and
          will contact you before your story appears.
        </Alert>

        {error && <div style={{ marginTop: 16 }}><Alert tone="danger">{error}</Alert></div>}

        <form className="ins-story-form" onSubmit={submit}>
          <Field label="Your name" htmlFor="s-name">
            <TextInput id="s-name" required maxLength={120} value={form.fullName}
              onChange={e => setForm({ ...form, fullName: e.target.value })} />
          </Field>

          <div className="grid grid-2">
            <Field label="Email" htmlFor="s-email" hint="An email or a phone number — either is enough.">
              <TextInput id="s-email" type="email" value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Phone" htmlFor="s-phone">
              <TextInput id="s-phone" inputMode="tel" value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })} />
            </Field>
          </div>

          <div className="grid grid-2">
            <Field label="What you do" htmlFor="s-role" hint="e.g. Landlord, Nakuru · Caretaker · Agent">
              <TextInput id="s-role" maxLength={80} value={form.roleLabel}
                onChange={e => setForm({ ...form, roleLabel: e.target.value })} />
            </Field>
            <Field label="Topic" htmlFor="s-cat">
              <Select id="s-cat" value={form.categoryId}
                onChange={e => setForm({ ...form, categoryId: e.target.value })}>
                <option value="">Choose a topic</option>
                {(cats.data?.categories ?? []).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            </Field>
          </div>

          <Field label="Give it a title" htmlFor="s-title">
            <TextInput id="s-title" required maxLength={160} value={form.title}
              placeholder="How I cut my vacancy rate in half"
              onChange={e => setForm({ ...form, title: e.target.value })} />
          </Field>

          <Field label="Your story" htmlFor="s-body"
            hint={words > 0 ? `${words} words — aim for 150 or more.` : 'What happened, and what you did.'}>
            <textarea id="s-body" className="input" rows={12} required minLength={40}
              value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
              placeholder="Start anywhere. What was the situation, what did you try, and what would you tell someone facing the same thing?" />
          </Field>

          <label className="ins-consent">
            <input type="checkbox" checked={consent} required
              onChange={e => setConsent(e.target.checked)} />
            <span>{CONSENT}</span>
          </label>

          <Button type="submit" loading={sending} disabled={!consent}>Send my story</Button>
        </form>
      </div>

      <SiteFooter />
    </>
  );
}
